import { useState, useEffect, useRef, Fragment } from 'react';
import ResourceFilter from '../resources/resource-filter';
import Select, { components } from 'react-select';
import { renderDataCentreBlocks } from '../../util/BlockUtil';
import {
  isItUndefined,
  fetchData,
  assemble,
  createFilterPressReleases,
  getAlgoliaDatasetValue,
  addQueryParamWithoutRedirecting,
  marketoOptions as options,
  getAlgoliaIndex,
  unixToDateString,
  correctUrlFormatForRouting,
  handleGoogleTagManager,
  addUserDataToSessionStorage,
  setSessionStorageUser,
  addUserDataToCookies,
  setCookieUser,
  replaceQueryParamWithoutRedirecting,
  pushSearchSuggestionClickEvent,
} from '../../util/DataUtil';
import { getLanguage } from '../../util/PageUtil';
import { CircularProgress } from '@mui/material';
import { useRouter } from 'next/router';
import { Each } from '../../util/Each';
import Link from 'next/link';
import PaginationLink from '../shared/PaginationLink';
import {
  buildPageHref,
  buildSearchHref,
  getPageFromQuery,
  getSearchFromQuery,
  hasMoreAlgoliaPages,
  stripListingParams,
} from '../../util/PaginationUtil';
import { SearchBarPlacement } from '../../constants/SearchBarPlacement';
import { FilterName} from "../../constants/FilterName";
import {
  sendClickedObjectIDsAfterSearch,
  sendNoResultsEvent,
} from '../../util/AlgoliaInsights';

const PressReleases = ({
  data,
  lang,
  width,
  global,
  popular_downloads_posts,
  related_content,
  tags,
}) => {
  const router = useRouter();
  // Seeded from the URL — see the note in handleSearch.
  const [query, setQuery] = useState(() => {
    const term = getSearchFromQuery(router?.query);
    return term ? [term] : [];
  });
  // Seeded from the URL — see the note in pageLayout/resources.js.
  const [page, setPage] = useState(() => getPageFromQuery(router?.query));
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [facetFilters, setFacetFilters] = useState([]);
  const [filterProps, setFilterProps] = useState([]);
  // Server-rendered first slice. The local/global variants stay client-side —
  // they depend on the visitor's language toggle, which the server cannot know.
  const serverResults = data?.initialPressReleases;
  const serverHits = Array.isArray(serverResults?.hits) ? serverResults.hits : [];

  const [showMore, setShowMore] = useState(serverHits);
  const [showMoreLocal, setShowMoreLocal] = useState([]);
  const [showMoreGlobal, setShowMoreGlobal] = useState([]);
  const [closeFilters, setCloseFilters] = useState(false);
  const [applyFilter, setApplyFilter] = useState(false);
  const [languageFilter, setLanguageFilter] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(false);
  const [algoliaData, setAlgoliaData] = useState(serverResults || []);
  const [algoliaDataLocal, setAlgoliaDataLocal] = useState([]);
  const [algoliaDataGlobal, setAlgoliaDataGlobal] = useState([]);
  const [localFilter, setLocalFilter] = useState(lang !== 'en');
  const [triggerLocalFilter, setTriggerLocalFilter] = useState(false);
  // const [isLocal, setIsLocal] = useState(lang != 'en' ? true : false);
  const [isLocal, setIsLocal] = useState(false);
  const [urlOfPage, setUrlOfPage] = useState('');
  const [selectedYear, setSelectedYear] = useState(null);
  let hitsPerPage = 16;
  let lng = lang;
  let facetFiltersArray = [];
  let lngArray = [];
  const [defaultYearToDisplay, setDefaultYearToDisplay] = useState(null);
  useEffect(() => {
    if (!lng) {
      return;
    }

    setIsLoading(true);
    // fetchAlgoliaData();

    // if(lng !== 'en'){
    //     fetchAlgoliaData(lng);
    // }

    fetchAlgoliaData(lng);
    // getMostPopularSearchTerms();
  }, [applyFilter, query, filterProps, triggerLocalFilter, isLocal]);

  // The slice for the requested page is already on the page from the server.
  // This effect appends, so letting its first run through would show the same
  // results twice.
  // Server-rendered results are always unfiltered, so when the URL carries a
  // filter or a search term the client has to fetch again — skipping that left
  // the filter showing as selected while the list underneath stayed unfiltered.
  const urlNarrowsResults =
    Object.keys(stripListingParams(router?.query) || {}).length > 0 ||
    Boolean(getSearchFromQuery(router?.query));

  const skipInitialPagination = useRef(
    serverHits.length > 0 && !urlNarrowsResults
  );

  useEffect(() => {
    if (!lng) {
      return;
    }

    if (skipInitialPagination.current) {
      skipInitialPagination.current = false;
      return;
    }

    if (page > 0) {
      fetchAlgoliaPagination();

      if (lng !== 'en') {
        fetchAlgoliaPagination(lng);
      }
    }
  }, [page]);

  // Hydrating the filters out of the URL is a one-time thing. This effect
  // re-runs whenever its dependencies settle — the author list arriving, for
  // instance — and on a later run it would read the URL again and re-apply it
  // on top of whatever the visitor had since selected, overwriting their choice
  // with a fresh mapping. That looked like the filter being applied and then
  // quietly ignored.
  const hydratedFromUrl = useRef(false);

  useEffect(() => {
    const { type, category } = router.query;
    const localFilter =
      type !== undefined ? type : category !== undefined ? category : '';
    let url = '';

    if (typeof window !== 'undefined') {
      url = window.location.pathname;
      setUrlOfPage(url);
      // The URL still carries a filter, so there is nothing to clear — whether
      // or not this run is the one that hydrated it. Reaching the clear branch
      // on a later run undid the filter the URL had just asked for.
      if (localFilter.length > 0) {
        if (!hydratedFromUrl.current) {
          hydratedFromUrl.current = true;
          triggerFilterFromRedirect([{ name: localFilter }]);
        }
      } else if (filterProps.length > 0 || query.length > 0) {
        // Only clear when something is actually applied.
        //
        // `clearAll` resets the page through `triggerFilter([])`, and this
        // effect re-runs whenever its dependencies settle — the author list
        // arriving, for instance. Guarding only the first run was not enough:
        // the second run wiped the page the URL had asked for, so after a
        // reload of `?page=3` the See More link pointed back at page two.
        //
        // With nothing applied there is nothing to clear, so the page the
        // visitor asked for survives.
        clearAll();
      }
    }
    const defaultYearToDisplayy = isItUndefined(
      data?.field_default_year_to_display,
      'name'
    );

    setDefaultYearToDisplay(defaultYearToDisplayy);

    if (defaultYearToDisplayy && !router.query.type) {
      setSelectedYear({
        label: defaultYearToDisplayy,
        value: defaultYearToDisplayy,
      });

      addQueryParamWithoutRedirecting(
        router,
        url,
        defaultYearToDisplayy,
        'type',
        // The default year is stamped in on load, not chosen — so whatever
        // page the visitor arrived on stays.
        true
      );
      triggerFilterFromRedirect([{ name: defaultYearToDisplayy }]);
    }

    // setCookieUser();
  }, []);

  const triggerFilterFromRedirect = (filter) => {
    for (let item of data?.categories) {
      // if(item.field_source_translation.toLowerCase().replace("&amp;", "&") === filter[0].name.toLowerCase()){
      if (
        item.name.toLowerCase().replace('&amp;', '&') ===
        filter[0].name.toLowerCase()
      ) {
        triggerFilter([item], false);
      }
    }
    setApplyFilter(true);
    // setQuery([]);
  };

  const fetchAlgoliaData = async (lng = 'en') => {
    setResults(
      (query.length || facetFilters.length || languageFilter.length) !== 0
    );

    const createFetchRequest = (lng, context) => {
      return createFilterPressReleases(
        lngArray,
        facetFiltersArray,
        facetFilters,
        query,
        hitsPerPage,
        page,
        lng,
        context,
        getAlgoliaDatasetValue(3) + getAlgoliaIndex()
      );
    };

    const fetchAlgolia = async (url, setData, setShowMoreFunc) => {
      try {
        const response = await fetch(url, options);
        const newResponse = await response.json();
        setShowMoreFunc(newResponse.hits);
        setData(newResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const url = createFetchRequest('en', undefined);
    const urlLocal = createFetchRequest(lng, 'local');
    const urlGlobal = createFetchRequest(lng, 'global');

    await Promise.all([
      fetchAlgolia(url, setAlgoliaData, setShowMore),
      fetchAlgolia(urlLocal, setAlgoliaDataLocal, setShowMoreLocal),
      fetchAlgolia(urlGlobal, setAlgoliaDataGlobal, setShowMoreGlobal),
    ]);
  };

  const fetchAlgoliaPagination = async (lng = 'en') => {
    const url = createFilterPressReleases(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      'en',
      undefined,
      getAlgoliaDatasetValue(3) + getAlgoliaIndex()
    );
    const urlLocal = createFilterPressReleases(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      'local',
      getAlgoliaDatasetValue(3) + getAlgoliaIndex()
    );
    const urlGlobal = createFilterPressReleases(
      lngArray,
      facetFiltersArray,
      facetFilters,
      query,
      hitsPerPage,
      page,
      lng,
      'global',
      getAlgoliaDatasetValue(3) + getAlgoliaIndex()
    );

    const fetchAlgolia = async (url, setShowMoreFunc, setAlgoliaDataFunc) => {
      try {
        const response = await fetch(url, options);
        const newResponse = await response.json();
        setShowMoreFunc((previousResponse) => [
          ...previousResponse,
          ...newResponse.hits,
        ]);
        setAlgoliaDataFunc(newResponse);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    await Promise.all([
      fetchAlgolia(url, setShowMore, setAlgoliaData),
      fetchAlgolia(urlLocal, setShowMoreLocal, setAlgoliaDataLocal),
      fetchAlgolia(urlGlobal, setShowMoreGlobal, setAlgoliaDataGlobal),
    ]);
  };

  const renderSearchResults = (data, queryID, length = -1) => {
    if (!data || data.length === 0) {
      return [];
    }

    data = Array.from(new Map(data.map((item) => [item.url, item])).values());

    return data.map(({ date, title, url, id, objectID }, index) => {
      const formattedUrl = correctUrlFormatForRouting(url);

      return (
        <Link passHref href={formattedUrl?.url} key={`press-release-${id || objectID || index}`}>
          <a
            className="result-item"
            onClick={() => {
              pushSearchSuggestionClickEvent(
                Array.isArray(query)
                  ? (query[0]?.toLowerCase() ?? '')
                  : (query?.toLowerCase() ?? ''),
                SearchBarPlacement.PRESS_RELEASES,
                title?.toLowerCase(),
                index + 1
              );
              sendClickedObjectIDsAfterSearch(queryID, objectID, getAlgoliaDatasetValue(3) + getAlgoliaIndex(), index + 1);
            }}
            // onClick={() => addUserDataToCookies(objectID, getAlgoliaDatasetValue(3) + getAlgoliaIndex(), 'manufacturing')}
          >
            {date && <div className="date">{unixToDateString(date)}</div>}
            <div className="title">{title}</div>
          </a>
        </Link>
      );
    });
  };

  function renderResults(data, queryID) {
    if (!data) {
      return;
    }

    if (data.length === 0) {
      if (query.length > 0) {
        sendNoResultsEvent(queryID, getAlgoliaDatasetValue(3) + getAlgoliaIndex());
      }
      return (
        <div className={'no-result'}>
          <h2>
            No results were found. Please check the spelling, reset any filters
            and try again.
          </h2>
          <div
            className={'clear-all'}
            onClick={() => {
              clearAll();
            }}
          >
            Clear all search terms.
          </div>
          {/* <div className={'most-popular'}>
                        <h2>Most popular</h2>
                        <div className={'search-results'}>{renderSearchCards(topSearchItems, 12)}</div>
                    </div> */}
        </div>
      );
    }

    return renderSearchResults(data, queryID);
  }

  const clearAll = () => {
    setFilterProps([]);
    triggerFilter([]);
    setLanguageFilter([]);
    if (query.length > 0) {
      setQuery([]);
    }
    setShowMore([]);
    setCloseFilters(false);
  };

  // `resetPage` is false when the filters are being hydrated from the URL
  // rather than chosen by the visitor. Someone opening a shared
  // `?type=blueprints&search=te&page=2` has not changed anything — treating
  // that as a filter change reset the page to one and stripped it from the
  // address before they saw it.
  const triggerFilter = (filters, resetPage = true) => {
    setFilterProps(filters);

    let searchFilters = [];
    for (let filter of filters) {
      searchFilters.push(filter?.field_source_translation);
    }

    setFacetFilters(searchFilters);
    // Back to the first page: the filtered set is a different, shorter list, so
    // staying on page three would ask for a slice of it that does not exist and
    // the results would come back empty.
    //
    // Skipped when `resetPage` is false — that is the URL being hydrated on
    // arrival, where the page in the address has to survive.
    if (resetPage) {
      setPage(0);
    }

    // The address itself is deliberately not touched here. The filter control
    // writes the URL and drops the page while doing it, and `router.asPath` is
    // still the pre-filter value at this point — rewriting from it rewound the
    // address and threw the filter straight back out.
    setApplyFilter(false);
  };

  const clearQuery = () => {
    setQuery([]);
    // Only when a term is actually in the URL. This is handed to the filter
    // component as a prop and fires while a filter is being applied — rewriting
    // the address unconditionally raced with the filter's own push and wiped
    // the filter straight back out of it.
    if (getSearchFromQuery(router.query)) {
      router.replace(buildSearchHref(router.asPath, ''), undefined, {
        shallow: true,
        scroll: false,
      });
    }
    setIsSearchActive(false);
    setIsFilterActive(true);
  };

  const handleSearch = (search) => {
    setQuery(search.length !== 0 ? [search] : []);
    // setFilterProps([]);
    // setFacetFilters([]);
    setPage(0);
    setApplyFilter(false);
    // Put the term in the address bar so a reload or a shared link restores the
    // same results and pagination composes with it. `buildSearchHref` drops the
    // page: a new search starts at the beginning.
    // Only when the term actually changes what is in the URL.
    //
    // This is handed to the filter component as `onSearchChange`, so it fires
    // while a filter is being applied — with an empty term and a `router.asPath`
    // that has not caught up with the filter's own push yet. Writing then
    // rewound the address to its pre-filter state and wiped the filter out.
    if (getSearchFromQuery(router.query) !== search) {
      router.replace(buildSearchHref(router.asPath, search), undefined, {
        shallow: true,
        scroll: false,
      });
    }
  };

  const handleLocalFilterChange = (value) => {
    setLocalFilter(value);
  };

  const getFilters = (categories) => {
    return categories
      ? categories
          .filter((item) => item?.parent_id?.length !== 0)
          .sort((a, b) => a.weight.localeCompare(b?.weight))
      : [];
  };

  const getFilterParent = (categories) => {
    return categories ? categories.filter((item) => !item?.parent_id) : [];
  };

  const handleLocalFilterSelect = (event) => {
    setFacetFilters(
      event.target.value !== -1 ? [event.target.value.toString()] : []
    );

    replaceQueryParamWithoutRedirecting(
      router,
      urlOfPage,
      'type',
      event.target.value.toString()
    );
    setTriggerLocalFilter(!triggerLocalFilter);
  };

  const getFilteredAlgoliaData = (data, regionFilter) => {
    if (!data) {
      return [];
    }

    if (regionFilter === 'global') {
      return data.filter((item) => item?.field_pr_original_language === 'en');
    } else if (regionFilter === 'local') {
      return data.filter((item) => item?.field_pr_original_language !== 'en');
    }

    return data;
  };

  return (
    <div id={'press-releases'} className={'press-releases'}>
      {renderDataCentreBlocks({
        data,
        lang,
        width,
        global,
        popular_downloads_posts,
        related_content,
        tags,
      })}
      <ResourceFilter
        metrosLabel={isItUndefined(global?.field_metro_label) || 'Metros'}
        data={assemble(data?.categories)}
        searchQuery={query}
        setIsSearchActive={setIsSearchActive}
        isSearchActive={isSearchActive}
        // search={isItUndefined(data.field_search_resources)}
        search={data?.field_search_resources[0]?.value}
        title={isItUndefined(data?.field_filter_resources)}
        filterProps={filterProps}
        // hideFilter
        isFilterActive={isFilterActive}
        setIsFilterActive={setIsFilterActive}
        triggerFilter={triggerFilter}
        handleFilterApply={setApplyFilter}
        onSearchChange={handleSearch}
        languageFilter={languageFilter}
        setLanguageFilter={setLanguageFilter}
        setCloseFilters={setCloseFilters}
        clearQuery={clearQuery}
        cancelText={isItUndefined(global?.field_cancel_text_translation)}
        applyFilterText={isItUndefined(global?.field_apply_filters_text)}
        clearAllText={isItUndefined(global?.field_clear_all_text)}
        selectedText={isItUndefined(global?.field_selected_text)}
        seeMoreText={isItUndefined(global?.field_see_more_text)}
        seeLessText={isItUndefined(global?.field_see_less)}
        placement={SearchBarPlacement.PRESS_RELEASES}
        dataLayerFilterName={FilterName.PRESS_RELEASES}
        // hideFilter={lang !== 'en'}
        // hideFilter={true}
      />
      <div className="inner-container">
        {/* PRESS RELEASES UPDATE */}

        {/* PRESS RELEASES UPDATE */}
        <div className="header-wrapper">
          {lang !== 'en' ? (
            <div className="tab-container">
              <span
                onClick={() => {
                  setIsLocal(false);
                  setPage(0);
                }}
                className={`${isLocal ? '' : 'active'}`}
              >
                {isItUndefined(data.field_global_filter_text)}
              </span>
              <div className="line" />
              <span
                className={`${isLocal ? 'active' : ''} `}
                onClick={() => {
                  setIsLocal(true);
                  setPage(0);
                }}
              >
                {isItUndefined(data.field_local_filter_text)}
              </span>
            </div>
          ) : null}
          {/* {
            // lang !== 'en' &&
            <div className="local-filter-wrapper">
              <div className={'select-wrapper'}>
                <Select
                  id="years"
                  name="years"
                  options={[
                    ...getFilters(data.categories).map((item) => ({
                      label: item.name,
                      value: item.name,
                    })),
                  ]}
                  value={selectedYear}
                  placeholder={
                    getFilterParent(data.categories)[0]?.name || 'Year'
                  }
                  onChange={(selectedOption) => {
                    setSelectedYear(selectedOption);
                    handleLocalFilterSelect({
                      target: {
                        name: 'years',
                        value: selectedOption.value,
                      },
                    });
                  }}
                  classNamePrefix="custom-react-select"
                  styles={{
                    control: (base) => ({
                      ...base,

                      border: '0',
                      boxShadow: 'none',
                      cursor: 'pointer',
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      padding: 0,
                      paddingRight: 10,
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0',
                      alignItems: 'center',
                      height: '100%',
                    }),

                    menu: (base) => ({
                      ...base,
                      zIndex: 99,
                    }),
                  }}
                />
              </div>
            </div>
          } */}
        </div>
        {isItUndefined(data.field_result_label) ? (
          <p className="results-title">
            {/* {isItUndefined(data.field_press_releases_title)} */}
            {isItUndefined(data.field_result_label)}
          </p>
        ) : null}
        <hr />
        {isLoading ? (
          <div className={'loader'}>
            <CircularProgress />
          </div>
        ) : (
          <Fragment>
            <div className={'search-results'}>
              {/* PRESS RELEASES UPDATE */}
              {
                localFilter && isLocal
                  ? // renderResults(getFilteredAlgoliaData(showMoreLocal, 'local'))
                    renderResults(showMoreLocal, algoliaDataLocal.queryID)
                  : renderResults(showMore, algoliaData.queryID)
                // lang !== 'en'
                // ?
                // renderResults(getFilteredAlgoliaData(showMoreLocal, 'global'))
                // renderResults(showMoreGlobal)
                // :
                // renderResults(showMore)
              }

              {/* PRESS RELEASES UPDATE */}
            </div>
            {/* PRESS RELEASES UPDATE */}
            {localFilter && isLocal
              ? hasMoreAlgoliaPages(algoliaDataLocal) && (
                  <PaginationLink page={page + 1} onLoadMore={setPage}>
                    See More
                  </PaginationLink>
                )
              : null}
            {hasMoreAlgoliaPages(algoliaData) && !isLocal && (
                <PaginationLink page={page + 1} onLoadMore={setPage}>
                  {isItUndefined(global?.field_see_more_text) || 'See More'}
                </PaginationLink>
              )}
            {/* PRESS RELEASES UPDATE */}
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default PressReleases;

export async function pressReleasesCategoriesProps(endpoint, lang) {
  return await fetchData(endpoint, 'press-releases-categories?lang=' + lang);
}
